/** @jsxRuntime classic */
/** @jsx jsx */

import { css, jsx } from '@emotion/react';
import { labelStyles } from '@zapier/style-encapsulation';

import { MEDIA_MIN_WIDTH } from '../../../constants';
import { Text } from './Text';

import { Animation } from './Animation';
import { ScrollFade } from './ScrollFade';

import { useAnimationsSectionContext } from './Context';
import { AnimationSection } from './types';

type Props = { sections: AnimationSection[] };

const Styles = labelStyles('AnimationsSection', {
  root: css({ width: '100%', [MEDIA_MIN_WIDTH.large]: { width: '45%' } }),
  scrollable: ({
    subNavHeight,
    universalNavHeight,
  }: {
    subNavHeight: number;
    universalNavHeight: number;
  }) =>
    css({
      minHeight: '75vh',
      display: 'flex',
      flexDirection: 'column',
      justifyContent: 'flex-start',
      [MEDIA_MIN_WIDTH.small]: {
        flexDirection: 'row',
        alignItems: 'center',
        justifyContent: 'space-between',
        minHeight: '45vh',
      },
      [MEDIA_MIN_WIDTH.large]: {
        minHeight: `calc(100vh - ${subNavHeight + universalNavHeight}px)`,
        paddingBottom: `${universalNavHeight}px`,
        '&:first-of-type': {
          marginTop: `${-universalNavHeight}px`,
          paddingBottom: '0px',
        },
        '&:last-of-type': {
          minHeight: '100vh',
          marginTop: `${-subNavHeight}px`,
          paddingBottom: '0px',
        },
      },
    }),
});

export function ScrollableSections({ sections }: Props) {
  const {
    animationRefs,
    animationRootMargin,
    isAnimationScrollable,
    sectionRefs,
    subNavHeight,
    universalNavHeight,
    updateSectionIndex,
  } = useAnimationsSectionContext();

  return (
    <div css={Styles.root}>
      {sections.map(
        (
          {
            animationAssetsUrl,
            animationDataUrl,
            animationSpeed,
            description,
            heading,
            mediaAltText,
            navLinkText,
          },
          index,
        ) => (
          <div
            css={Styles.scrollable({ universalNavHeight, subNavHeight })}
            id={navLinkText.toLocaleLowerCase().replace(' ', '-')}
            key={`${animationDataUrl}-${navLinkText}-${index}`}
            ref={(ref) => {
              if (ref) {
                sectionRefs.current[index] = ref;
              }
            }}
          >
            <Text description={description} heading={heading} index={index} />
            {isAnimationScrollable && (
              <ScrollFade
                onVisible={() => updateSectionIndex(index)}
                rootMargin={animationRootMargin}
              >
                <Animation
                  animationAssetsUrl={animationAssetsUrl}
                  animationDataUrl={animationDataUrl}
                  animationSpeed={animationSpeed}
                  ariaLabel={mediaAltText}
                  autoPlay={index === 0}
                  isVisible={true}
                  ref={(ref) => {
                    if (ref) animationRefs.current[index] = ref;
                  }}
                />
              </ScrollFade>
            )}
          </div>
        ),
      )}
    </div>
  );
}
